import { useEffect, useMemo, useRef, useState } from 'react';
import { ref, remove, update } from 'firebase/database';
import { FaPaperPlane, FaPen, FaTrash, FaTimes } from 'react-icons/fa';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import {
  WorkspaceEmptyState,
  WorkspacePageError,
  WorkspacePageLoader,
} from '../ui/Workspace';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { useAdminBatchLedger } from '../../hooks/useLmsData';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { markBatchAttendance, recordBatchPayment } from '../../utils/lmsMutations';
import { formatStudentName } from '../../utils/lmsSelectors';
import {
  getSuspensionLabel,
  SUSPENSION_LEVELS,
  suspensionMessage,
} from '../../utils/accessSuspensions';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAY_INDEX = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

function getNearestPeriodKey(periods) {
  if (!periods.length) return '';
  const today = new Date();
  const current = periods.find((period) => dateInRange(today, period.start, period.end));
  if (current) return current.key;

  return periods
    .slice()
    .sort(
      (a, b) =>
        Math.abs(a.start.getTime() - today.getTime()) -
        Math.abs(b.start.getTime() - today.getTime()),
    )[0].key;
}

const BatchStudentManagement = () => {
  const { currentUser } = useAuth();
  const { data, loading, error, refresh } = useAdminBatchLedger();
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedBatchKey, setSelectedBatchKey] = useState('');
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');
  const [activeCell, setActiveCell] = useState(null);
  const [quickAction, setQuickAction] = useState(null);
  const [highlightedPeriodKey, setHighlightedPeriodKey] = useState('');
  const [attendanceOverrides, setAttendanceOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [feeSaving, setFeeSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [feeError, setFeeError] = useState('');

  const courseList = useMemo(() => data?.courseList || [], [data]);
  const selectedCourse = useMemo(
    () => courseList.find((course) => course.id === selectedCourseId) || null,
    [courseList, selectedCourseId],
  );
  const batchOptions = useMemo(
    () =>
      (selectedCourse?.seatSummary || []).map((batch) => ({
        key: String(batch.batchIndex),
        label: `${batch.batchId || `Batch ${batch.batchIndex + 1}`} - ${
          batch.branchName || batch.branch || 'Branch not set'
        }`,
        batch,
      })),
    [selectedCourse],
  );
  const selectedBatch =
    batchOptions.find((option) => option.key === selectedBatchKey)?.batch ||
    batchOptions[0]?.batch ||
    null;
  const periods = useMemo(
    () => buildLedgerPeriods(selectedCourse, selectedBatch),
    [selectedCourse, selectedBatch],
  );
  const nearestPeriodKey = useMemo(() => getNearestPeriodKey(periods), [periods]);

  useEffect(() => {
    if (!courseList.length || selectedCourseId) return;
    setSelectedCourseId(courseList[0].id);
  }, [courseList, selectedCourseId]);

  useEffect(() => {
    if (!batchOptions.length) {
      setSelectedBatchKey('');
      return;
    }
    if (!batchOptions.some((option) => option.key === selectedBatchKey)) {
      setSelectedBatchKey(batchOptions[0].key);
    }
  }, [batchOptions, selectedBatchKey]);

  useEffect(() => {
    if (!periods.length) {
      setHighlightedPeriodKey('');
      return;
    }
    if (!periods.some((period) => period.key === highlightedPeriodKey)) {
      setHighlightedPeriodKey(nearestPeriodKey || periods[0].key);
    }
  }, [highlightedPeriodKey, nearestPeriodKey, periods]);

  const rows = useMemo(() => {
    if (!selectedCourse || !selectedBatch || !periods.length) return [];
    return buildStudentRows({
      course: selectedCourse,
      batch: selectedBatch,
      periods,
      students: data?.students || {},
      users: data?.users || {},
      payments: data?.payments || [],
      paymentRequests: data?.paymentRequests || [],
      attendanceByStudent: mergeAttendanceOverrides(
        data?.attendanceByStudent || {},
        attendanceOverrides,
      ),
    });
  }, [attendanceOverrides, data, periods, selectedBatch, selectedCourse]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const visible = rows.filter((row) => {
      const matchesSearch =
        !needle ||
        (searchField !== 'id' && row.studentName.toLowerCase().includes(needle)) ||
        (searchField !== 'name' &&
          (row.academyStudentId.toLowerCase().includes(needle) ||
            row.phone.toLowerCase().includes(needle)));
      if (!matchesSearch) return false;
      if (filter === 'due') return row.cells.some((cell) => cell.paymentStatus === 'due');
      if (filter === 'paid') return row.cells.every((cell) => cell.paymentStatus === 'paid');
      if (filter === 'no-dues') return row.outstandingTotal <= 0;
      if (filter === 'has-paid') return row.completedPaymentTotal > 0;
      if (filter === 'unpaid') return row.completedPaymentTotal <= 0;
      if (filter === 'missing-attendance') {
        return row.cells.some((cell) =>
          cell.weekCells.some(
            (weekCell) =>
              weekCell.isCurrentOrPast && weekCell.attendanceStatus === 'unmarked',
          ),
        );
      }
      if (filter === 'current-month') {
        return row.cells.some((cell) => cell.isCurrent && cell.isCurrentOrPast);
      }
      return true;
    });
    return sortRows(visible, sortBy);
  }, [filter, rows, search, searchField, sortBy]);

  const studentSearchSuggestions = useMemo(
    () =>
      rows
        .flatMap((row) => [
          row.academyStudentId,
          row.phone,
          searchField !== 'id' ? row.studentName : '',
        ])
        .filter(Boolean)
        .slice(0, 80),
    [rows, searchField],
  );

  const stats = useMemo(() => {
    const paidTotal = rows.reduce((sum, row) => sum + row.completedPaymentTotal, 0);
    const dueTotal = rows.reduce((sum, row) => sum + row.outstandingTotal, 0);
    const attendanceThisMonth = rows.reduce(
      (sum, row) =>
        sum +
        row.cells.reduce(
          (count, cell) =>
            count +
            cell.weekCells.filter(
              (weekCell) =>
                cell.isCurrent && weekCell.attendanceStatus !== 'unmarked',
            ).length,
          0,
        ),
      0,
    );
    return {
      students: rows.length,
      paidTotal,
      dueTotal,
      attendanceThisMonth,
    };
  }, [rows]);

  const handleSaveCell = async (payload, contextOverride = null) => {
    const cellContext = contextOverride || activeCell;
    if (!cellContext) return;
    setSaving(true);
    setFormError('');
    const hasAttendanceUpdate = Boolean(payload.attendanceStatus);
    const hasPaymentUpdate = Number(payload.paymentAmount || 0) > 0;
    const attendancePayload = hasAttendanceUpdate
      ? {
          studentId: cellContext.row.studentId,
          academyStudentId: cellContext.row.academyStudentId,
          studentName: cellContext.row.studentName,
          courseId: selectedCourse.id,
          courseName: selectedCourse.name || selectedCourse.title || 'Course',
          batchId: selectedBatch.batchId ?? selectedBatch.batchIndex,
          batchName: selectedBatch.batchId || `Batch ${selectedBatch.batchIndex + 1}`,
          branch: selectedBatch.branchName || selectedBatch.branch || '',
          periodStart: cellContext.period.startKey,
          periodEnd: cellContext.period.endKey,
          status: payload.attendanceStatus,
          notes: payload.attendanceNotes,
          operator: currentUser,
        }
      : null;

    if (hasAttendanceUpdate && !hasPaymentUpdate) {
      setAttendanceOverrides((current) =>
        applyAttendanceOverride(current, attendancePayload),
      );
    }

    try {
      if (attendancePayload) {
        await markBatchAttendance(attendancePayload);
        if (hasPaymentUpdate) {
          setAttendanceOverrides((current) =>
            applyAttendanceOverride(current, attendancePayload),
          );
        }
      }

      if (hasPaymentUpdate) {
        await recordBatchPayment({
          studentId: cellContext.row.studentId,
          studentName: cellContext.row.studentName,
          courseId: selectedCourse.id,
          courseName: selectedCourse.name || selectedCourse.title || 'Course',
          batchId: selectedBatch.batchId ?? selectedBatch.batchIndex,
          batchName: selectedBatch.batchId || `Batch ${selectedBatch.batchIndex + 1}`,
          amount: Number(payload.paymentAmount || 0),
          paymentMethod: payload.paymentMethod,
          receiptNumber: payload.receiptNumber,
          receiptFile: payload.receiptFile,
          notes: payload.paymentNotes,
          paymentCycle: cellContext.month.startKey,
          paymentCycleLabel: cellContext.month.label,
          operator: currentUser,
        });
      }

      if (hasPaymentUpdate) {
        await refresh();
      }
    } catch (err) {
      setFormError(err.message || 'Could not save this ledger update.');
      if (hasAttendanceUpdate && !hasPaymentUpdate) {
        setAttendanceOverrides((current) => {
          const next = { ...current };
          delete next[buildAttendanceOverrideKey(attendancePayload)];
          return next;
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePayment = async (payment, payload) => {
    if (!payment?.id) return;
    const amount = Number(payload.paymentAmount || 0);
    if (amount <= 0) {
      setFormError('Payment amount must be greater than zero.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const updates = {
        amount,
        paymentMethod: payload.paymentMethod,
        receiptNumber: payload.receiptNumber || '',
        notes: payload.paymentNotes || '',
        updatedAt: new Date().toISOString(),
        updatedById: currentUser?.uid || '',
        updatedByName:
          currentUser?.displayName || currentUser?.email || 'Admin',
      };
      await Promise.all([
        update(ref(db, `payments/${payment.id}`), updates),
        update(ref(db, `verifiedPayments/${payment.id}`), updates),
      ]);
      await refresh();
    } catch (paymentError) {
      console.error('Failed to update payment:', paymentError);
      setFormError(paymentError?.message || 'Could not update this payment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!payment?.id) return;
    if (!window.confirm(`Remove payment LKR ${formatAmount(payment.amount)}?`)) return;
    setSaving(true);
    setFormError('');
    try {
      await Promise.all([
        remove(ref(db, `payments/${payment.id}`)),
        remove(ref(db, `verifiedPayments/${payment.id}`)),
      ]);
      await refresh();
    } catch (paymentError) {
      console.error('Failed to remove payment:', paymentError);
      setFormError(paymentError?.message || 'Could not remove this payment.');
    } finally {
      setSaving(false);
    }
  };

  const handleStudentAccessChange = async ({ row, suspended, reason, level }) => {
    if (!row?.studentId) return;
    setSaving(true);
    setFormError('');
    try {
      const timestamp = new Date().toISOString();
      const suspensionLevel = level || SUSPENSION_LEVELS.COURSE;
      if (suspensionLevel !== SUSPENSION_LEVELS.PLATFORM) {
        const path = `${selectedCourse.id}`;
        if (suspended) {
          const record = {
            level: suspensionLevel,
            courseId: selectedCourse.id,
            courseName: selectedCourse.name || selectedCourse.title || 'Course',
            batchId: selectedBatch.batchId ?? selectedBatch.batchIndex,
            batchName: selectedBatch.batchId || `Batch ${selectedBatch.batchIndex + 1}`,
            reason: reason || 'Administrative suspension',
            suspendedAt: timestamp,
            suspendedBy: currentUser?.uid || '',
            suspendedByName:
              currentUser?.displayName || currentUser?.email || 'Admin',
          };
          const updates = {
            [`students/${row.studentId}/courseSuspensions/${path}`]: record,
            [`users/${row.studentId}/courseSuspensions/${path}`]: record,
          };
          if (row.enrollment?.id) {
            updates[`enrollments/${row.enrollment.id}/courseSuspensions/${path}`] =
              record;
          }
          await update(ref(db), updates);
        } else {
          await Promise.all([
            remove(ref(db, `students/${row.studentId}/courseSuspensions/${path}`)),
            remove(ref(db, `users/${row.studentId}/courseSuspensions/${path}`)),
            row.enrollment?.id
              ? remove(
                  ref(
                    db,
                    `enrollments/${row.enrollment.id}/courseSuspensions/${path}`,
                  ),
                )
              : Promise.resolve(),
          ]);
        }
        await refresh();
        return;
      }

      const updates = suspended
        ? {
            status: 'suspended',
            accessSuspended: true,
            suspensionLevel: SUSPENSION_LEVELS.PLATFORM,
            suspensionReason: reason || 'Administrative suspension',
            suspendedAt: timestamp,
            suspendedBy: currentUser?.uid || '',
            suspendedByName: currentUser?.displayName || currentUser?.email || 'Admin',
          }
        : {
            status: 'active',
            accessSuspended: false,
            suspensionLevel: null,
            suspensionReason: null,
            suspensionLiftedAt: timestamp,
            suspensionLiftedBy: currentUser?.uid || '',
            suspensionLiftedByName:
              currentUser?.displayName || currentUser?.email || 'Admin',
          };
      const multiPathUpdates = Object.fromEntries(
        Object.entries(updates).flatMap(([key, value]) => [
          [`students/${row.studentId}/${key}`, value],
          [`users/${row.studentId}/${key}`, value],
        ]),
      );
      await update(ref(db), multiPathUpdates);
      await refresh();
    } catch (accessError) {
      console.error('Failed to update student access:', accessError);
      setFormError(accessError?.message || 'Could not update student access.');
    } finally {
      setSaving(false);
    }
  };

  const openLedgerDetails = (context) => {
    setFormError('');
    setQuickAction(null);
    setActiveCell(context);
  };

  const handleSaveCourseFees = async ({ fullFee, registrationFee }) => {
    if (!selectedCourse?.id) return;
    setFeeSaving(true);
    setFeeError('');
    try {
      const normalizedFullFee = Number(fullFee || 0);
      const normalizedRegistrationFee = Number(registrationFee || 0);
      await update(ref(db, `courses/${selectedCourse.id}`), {
        fees: {
          ...(selectedCourse.fees || {}),
          fullCourseFee: normalizedFullFee,
          registrationFee: normalizedRegistrationFee,
        },
        oneoffPayment: normalizedFullFee,
      });
      await refresh();
      return true;
    } catch (err) {
      setFeeError(err.message || 'Could not update course fees.');
      return false;
    } finally {
      setFeeSaving(false);
    }
  };

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>{error.message}</WorkspacePageError>;

  return (
    <>
      <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
        <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                Workspace
              </p>
              <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
                Batch Ledger
              </h1>
            </div>
            <div className="grid flex-1 gap-2 sm:grid-cols-2 xl:max-w-3xl xl:grid-cols-2">
              <CompactField label="Course">
                <select
                  value={selectedCourseId}
                  onChange={(event) => {
                    setSelectedCourseId(event.target.value);
                    setSelectedBatchKey('');
                  }}
                  className="ledger-input min-h-[38px]"
                >
                  {courseList.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name || course.title || 'Course'}
                    </option>
                  ))}
                </select>
              </CompactField>
              <CompactField label="Batch">
                <select
                  value={selectedBatchKey}
                  onChange={(event) => setSelectedBatchKey(event.target.value)}
                  className="ledger-input min-h-[38px]"
                  disabled={!batchOptions.length}
                >
                  {batchOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </CompactField>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <CompactStat label="Students" value={stats.students} />
            <CompactStat label="Paid" value={`LKR ${formatAmount(stats.paidTotal)}`} tone="success" />
            <CompactStat label="Due" value={`LKR ${formatAmount(stats.dueTotal)}`} tone={stats.dueTotal ? 'warning' : 'success'} />
            <CompactStat label="Attendance" value={stats.attendanceThisMonth} />
          </div>
          <CourseBatchSummary
            course={selectedCourse}
            batch={selectedBatch}
            periods={periods}
            onSaveFees={handleSaveCourseFees}
            saving={feeSaving}
            error={feeError}
          />
        </header>

        <section className="rounded-lg border border-stone-200 bg-white">
          <div className="flex flex-col gap-2 border-b border-stone-200 px-3 py-2">
            <div>
              <h2 className="text-base font-semibold tracking-tight text-stone-950">
                Student matrix
              </h2>
              <p className="text-xs text-stone-500">
                One row per student. Attendance slots follow the batch class days; payments apply to the full month.
              </p>
            </div>
            <div className="grid gap-2 lg:grid-cols-[110px_minmax(180px,1fr)_150px_160px_auto] lg:items-end">
              <CompactField label="Search by">
                <select
                  value={searchField}
                  onChange={(event) => setSearchField(event.target.value)}
                  className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
                >
                  <option value="all">Name or ID</option>
                  <option value="name">Name</option>
                  <option value="id">Student ID</option>
                </select>
              </CompactField>
              <CompactField label="Student">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search student"
                  list="batch-ledger-student-suggestions"
                  className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
                />
                <datalist id="batch-ledger-student-suggestions">
                  {studentSearchSuggestions.map((item, index) => (
                    <option key={`${item}-${index}`} value={item} />
                  ))}
                </datalist>
              </CompactField>
              <CompactField label="Filter">
                <select
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
                >
                  <option value="all">All students</option>
                  <option value="due">Has dues</option>
                  <option value="no-dues">No dues</option>
                  <option value="has-paid">Has payments</option>
                  <option value="unpaid">No payments</option>
                  <option value="paid">All months paid</option>
                  <option value="missing-attendance">Attendance missing</option>
                  <option value="current-month">Current month</option>
                </select>
              </CompactField>
              <CompactField label="Sort">
                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
                >
                  <option value="name-asc">Name A-Z</option>
                  <option value="id-asc">Student ID</option>
                  <option value="paid-desc">Paid high-low</option>
                  <option value="paid-asc">Paid low-high</option>
                  <option value="due-desc">Due high-low</option>
                  <option value="due-asc">Due low-high</option>
                </select>
              </CompactField>
              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <Legend />
              </div>
            </div>
          </div>
          <div className="p-2">
            {!selectedCourse ? (
              <WorkspaceEmptyState
                title="No courses found"
                message="Create a course and batch before opening the ledger."
              />
            ) : !periods.length ? (
              <WorkspaceEmptyState
                title="Batch dates are incomplete"
                message="Set a batch start date and course duration to generate monthly ledger columns."
              />
            ) : !filteredRows.length ? (
              <WorkspaceEmptyState
                title="No students in this view"
                message="Change the search, filter, or selected batch."
              />
            ) : (
              <LedgerTable
                periods={periods}
                rows={filteredRows}
                highlightedPeriodKey={highlightedPeriodKey}
                onHighlightPeriod={setHighlightedPeriodKey}
                onCellClick={(row, cell, weekCell) => {
                  openLedgerDetails({
                    row,
                    cell,
                    weekCell,
                    month: cell.period,
                    period: weekCell?.period || cell.period,
                  });
                }}
                onCellContextMenu={(event, row, cell, weekCell) => {
                  event.preventDefault();
                  setFormError('');
                  setActiveCell(null);
                  setQuickAction({
                    x: event.clientX,
                    y: event.clientY,
                    context: {
                      row,
                      cell,
                      weekCell,
                      month: cell.period,
                      period: weekCell?.period || cell.period,
                    },
                  });
                }}
              />
            )}
          </div>
        </section>
      </div>

      <CellEditorModal
        open={Boolean(activeCell)}
        onClose={() => setActiveCell(null)}
        context={activeCell}
        rows={filteredRows}
        onChangeContext={(nextRow) => {
          setFormError('');
          setActiveCell((current) => buildModalContext(nextRow, current));
        }}
        onSave={handleSaveCell}
        onUpdatePayment={handleUpdatePayment}
        onDeletePayment={handleDeletePayment}
        onStudentAccessChange={handleStudentAccessChange}
        saving={saving}
        error={formError}
      />
      <QuickActionMenu
        state={quickAction}
        onClose={() => setQuickAction(null)}
        onOpenDetails={() => quickAction && openLedgerDetails(quickAction.context)}
        onSave={async (payload) => {
          if (!quickAction) return;
          await handleSaveCell(payload, quickAction.context);
          setQuickAction(null);
        }}
        saving={saving}
      />
    </>
  );
};

const useCompactLedgerLayout = () => {
  const [compact, setCompact] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = () => setCompact(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return compact;
};

const LedgerTable = ({
  periods,
  rows,
  highlightedPeriodKey,
  onHighlightPeriod,
  onCellClick,
  onCellContextMenu,
}) => {
  const scrollRef = useRef(null);
  const compactLayout = useCompactLedgerLayout();

  useEffect(() => {
    if (compactLayout || !highlightedPeriodKey || !scrollRef.current) return;
    const column = scrollRef.current.querySelector(
      `[data-period-key="${highlightedPeriodKey}"]`,
    );
    if (!column) return;
    const left =
      column.offsetLeft -
      scrollRef.current.clientWidth / 2 +
      column.clientWidth / 2;
    scrollRef.current.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [compactLayout, highlightedPeriodKey]);

  if (compactLayout) {
    return (
      <LedgerMobileList
        periods={periods}
        rows={rows}
        highlightedPeriodKey={highlightedPeriodKey}
        onHighlightPeriod={onHighlightPeriod}
        onCellClick={onCellClick}
        onCellContextMenu={onCellContextMenu}
      />
    );
  }

  return (
    <div ref={scrollRef} className="overflow-x-auto rounded-md border border-stone-200">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 z-30 w-[220px] min-w-[220px] border-b border-r border-stone-200 bg-stone-50 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500 shadow-[inset_-1px_0_0_#e7e5e4]">
              Student
            </th>
            <th className="sticky left-[220px] z-30 w-[88px] min-w-[88px] border-b border-r border-stone-200 bg-stone-50 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500 shadow-[inset_-1px_0_0_#e7e5e4]">
              Paid
            </th>
            <th className="sticky left-[308px] z-30 w-[88px] min-w-[88px] border-b border-r border-stone-200 bg-stone-50 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500 shadow-[inset_-1px_0_0_#e7e5e4]">
              Due
            </th>
            {periods.map((period) => {
              const highlighted = period.key === highlightedPeriodKey;
              const columnWidth = Math.max(132, period.weeks.length * 34);
              return (
              <th
                key={period.key}
                data-period-key={period.key}
                className={`min-w-[132px] border-b border-r border-stone-200 px-1 py-0 text-center ${
                  highlighted ? 'bg-[#fbcfe8]' : 'bg-stone-50'
                }`}
                style={{ minWidth: `${columnWidth}px` }}
              >
                <button
                  type="button"
                  onClick={() => onHighlightPeriod(period.key)}
                  className={`block w-full rounded px-2 py-1.5 text-xs font-semibold transition ${
                    highlighted
                      ? 'bg-[#fdf2f8] text-[#9d174d] ring-1 ring-[#db2777]'
                      : 'text-stone-700 hover:bg-white'
                  }`}
                >
                  {period.label}
                </button>
              </th>
            )})}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.studentId} className="group">
              <td className="sticky left-0 z-20 w-[220px] min-w-[220px] border-b border-r border-stone-200 bg-white px-2 py-0.5 shadow-[inset_-1px_0_0_#e7e5e4] group-hover:bg-stone-50">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-stone-950">
                    {row.studentName}
                  </p>
                  <p className="truncate text-[11px] text-stone-500">
                    {row.academyStudentId || row.phone || 'No ID'}
                  </p>
                </div>
              </td>
              <td className="sticky left-[220px] z-20 w-[88px] min-w-[88px] border-b border-r border-stone-200 bg-white px-2 py-0.5 text-left text-[11px] font-semibold text-stone-950 shadow-[inset_-1px_0_0_#e7e5e4] group-hover:bg-stone-50">
                LKR {formatAmount(row.completedPaymentTotal)}
              </td>
              <td className="sticky left-[308px] z-20 w-[88px] min-w-[88px] border-b border-r border-stone-200 bg-white px-2 py-0.5 text-left text-[11px] font-semibold text-stone-950 shadow-[inset_-1px_0_0_#e7e5e4] group-hover:bg-stone-50">
                LKR {formatAmount(row.outstandingTotal)}
              </td>
              {row.cells.map((cell) => (
                <td
                  key={cell.period.key}
                  className={`border-b border-r border-stone-200 p-0.5 ${
                    cell.period.key === highlightedPeriodKey ? 'bg-[#fce7f3]' : 'bg-white'
                  }`}
                  style={{ minWidth: `${Math.max(132, cell.weekCells.length * 34)}px` }}
                >
                  <div
                    className={`rounded border p-0.5 ${cellClass(cell.paymentStatus)}`}
                    title={`${cell.period.label}: LKR ${formatAmount(cell.paidAmount)} / ${formatAmount(cell.dueAmount)}`}
                  >
                    <div
                      className="grid gap-0.5"
                      style={{
                        gridTemplateColumns: `repeat(${cell.weekCells.length}, minmax(0, 1fr))`,
                      }}
                    >
                      {cell.weekCells.map((weekCell) => (
                        <button
                          key={weekCell.period.key}
                          type="button"
                          onClick={() => onCellClick(row, cell, weekCell)}
                          onContextMenu={(event) =>
                            onCellContextMenu(event, row, cell, weekCell)
                          }
                          className={`min-h-[24px] rounded border text-[10px] font-semibold transition hover:border-stone-600 ${attendanceSlotClass(
                            weekCell.attendanceStatus,
                          )}`}
                          aria-label={`${row.studentName} ${cell.period.label} ${formatShortDate(
                            weekCell.period.start,
                          )} to ${formatShortDate(weekCell.period.end)}`}
                        >
                          {attendanceSlotText(weekCell)}
                        </button>
                      ))}
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LedgerMobileList = ({
  periods,
  rows,
  highlightedPeriodKey,
  onHighlightPeriod,
  onCellClick,
  onCellContextMenu,
}) => (
  <div className="space-y-3">
    <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs leading-5 text-stone-600">
      Each student card shows totals at the top. Swipe the month row horizontally to
      mark attendance or record payments.
    </p>

    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {periods.map((period) => {
        const highlighted = period.key === highlightedPeriodKey;
        return (
          <button
            key={period.key}
            type="button"
            onClick={() => onHighlightPeriod(period.key)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              highlighted
                ? 'bg-[#db2777] text-white'
                : 'border border-stone-200 bg-white text-stone-700'
            }`}
          >
            {period.label}
          </button>
        );
      })}
    </div>

    <div className="space-y-3">
      {rows.map((row) => (
        <article
          key={row.studentId}
          className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm"
        >
          <header className="border-b border-stone-200 bg-stone-50 px-3 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-stone-950">
                  {row.studentName}
                </p>
                <p className="mt-0.5 truncate text-xs text-stone-500">
                  {row.academyStudentId || row.phone || 'No student ID'}
                </p>
              </div>
              <div className="shrink-0 text-right text-[11px] leading-5 text-stone-700">
                <p>
                  <span className="text-stone-500">Paid</span>{' '}
                  <span className="font-semibold text-stone-950">
                    LKR {formatAmount(row.completedPaymentTotal)}
                  </span>
                </p>
                <p>
                  <span className="text-stone-500">Due</span>{' '}
                  <span className="font-semibold text-stone-950">
                    LKR {formatAmount(row.outstandingTotal)}
                  </span>
                </p>
              </div>
            </div>
          </header>

          <div className="overflow-x-auto">
            <div className="inline-flex min-w-full gap-2 p-3">
              {row.cells.map((cell) => {
                const highlighted = cell.period.key === highlightedPeriodKey;
                return (
                  <div
                    key={cell.period.key}
                    className={`w-[min(72vw,220px)] shrink-0 rounded-lg border p-2 ${
                      highlighted
                        ? 'border-[#db2777] bg-[#fdf2f8]'
                        : 'border-stone-200 bg-white'
                    }`}
                  >
                    <p className="text-xs font-semibold text-stone-800">{cell.period.label}</p>
                    <p className="mt-0.5 text-[10px] text-stone-500">
                      {paymentLabel(cell)} · LKR {formatAmount(cell.paidAmount)}
                    </p>
                    <div
                      className={`mt-2 rounded border p-1 ${cellClass(cell.paymentStatus)}`}
                    >
                      <div
                        className="grid gap-1"
                        style={{
                          gridTemplateColumns: `repeat(${cell.weekCells.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {cell.weekCells.map((weekCell) => (
                          <button
                            key={weekCell.period.key}
                            type="button"
                            onClick={() => onCellClick(row, cell, weekCell)}
                            onContextMenu={(event) =>
                              onCellContextMenu(event, row, cell, weekCell)
                            }
                            className={`min-h-[28px] rounded border text-[10px] font-semibold transition hover:border-stone-600 ${attendanceSlotClass(
                              weekCell.attendanceStatus,
                            )}`}
                            aria-label={`${row.studentName} ${cell.period.label}`}
                          >
                            {attendanceSlotText(weekCell)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </article>
      ))}
    </div>
  </div>
);

const buildModalContext = (row, current) => {
  if (!row || !current) return current;
  const cell =
    row.cells.find((item) => item.period.key === current.month?.key) ||
    row.cells.find((item) => item.period.key === current.cell?.period?.key) ||
    row.cells[0];
  const weekCell =
    cell?.weekCells.find((item) => item.period.key === current.period?.key) ||
    cell?.weekCells.find((item) => item.weekNumber === current.weekCell?.weekNumber) ||
    cell?.weekCells[0];
  if (!cell || !weekCell) return current;
  return {
    row,
    cell,
    weekCell,
    month: cell.period,
    period: weekCell.period,
  };
};

const QuickActionMenu = ({ state, onClose, onOpenDetails, onSave, saving }) => {
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (!state) return;
    setAmount('');
  }, [state]);

  useEffect(() => {
    if (!state) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-quick-action-menu="true"]')) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handlePointerDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose, state]);

  if (!state) return null;

  const { context, x, y } = state;
  const positionStyle = {
    left: Math.min(x, window.innerWidth - 280),
    top: Math.min(y, window.innerHeight - 260),
  };

  const saveAttendance = (attendanceStatus) =>
    onSave({
      attendanceStatus,
      attendanceNotes: '',
      paymentAmount: '',
      paymentMethod: 'cash',
      receiptNumber: '',
      receiptFile: null,
      paymentNotes: '',
    });

  return (
    <div
      data-quick-action-menu="true"
      className="fixed z-[60] w-[260px] rounded-2xl border border-stone-200 bg-white p-3 text-sm shadow-2xl"
      style={positionStyle}
    >
      <div className="border-b border-stone-100 pb-2">
        <p className="truncate font-semibold text-stone-950">
          {context.row.studentName}
        </p>
        <p className="mt-0.5 text-xs text-stone-500">
          {context.month.label} / {context.period.label}
        </p>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {[
          ['present', 'Present'],
          ['absent', 'Absent'],
          ['clear', 'Clear'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => saveAttendance(value)}
            disabled={saving}
            className="min-h-[34px] rounded-lg border border-stone-200 px-2 text-xs font-semibold text-stone-700 transition hover:border-stone-400 hover:bg-stone-50 disabled:opacity-50"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-3 rounded-xl bg-stone-50 p-2.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Record payment
        </p>
        <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          <input
            type="number"
            min="0"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Amount"
            className="min-h-[34px] rounded-lg border border-stone-300 px-2 text-sm focus:border-[#d61f74] focus:outline-none focus:ring-1 focus:ring-[#d61f74]"
          />
          <button
            type="button"
            onClick={() =>
              onSave({
                attendanceStatus: '',
                attendanceNotes: '',
                paymentAmount: amount,
                paymentMethod: 'cash',
                receiptNumber: '',
                receiptFile: null,
                paymentNotes: '',
              })
            }
            disabled={saving || Number(amount || 0) <= 0}
            className="min-h-[34px] rounded-lg bg-stone-950 px-3 text-xs font-semibold text-white disabled:bg-stone-300"
          >
            Save
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenDetails}
        className="mt-2 flex min-h-[36px] w-full items-center justify-center rounded-lg border border-stone-200 bg-white px-3 text-xs font-semibold text-stone-700 transition hover:bg-stone-50"
      >
        Open Ledger Entry Details
      </button>
    </div>
  );
};

const CellEditorModal = ({
  open,
  onClose,
  context,
  rows = [],
  onChangeContext,
  onSave,
  onUpdatePayment,
  onDeletePayment,
  onStudentAccessChange,
  saving,
  error,
}) => {
  const { methods } = usePaymentMethods('reception');
  const [attendanceStatus, setAttendanceStatus] = useState('');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionLevel, setSuspensionLevel] = useState(SUSPENSION_LEVELS.COURSE);
  const [showSuspensionControls, setShowSuspensionControls] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAttendanceStatus(context?.weekCell?.attendanceStatus || '');
    setAttendanceNotes(context?.weekCell?.attendanceRecord?.notes || '');
    setPaymentAmount('');
    setPaymentMethod(methods[0]?.type || methods[0]?.id || 'cash');
    setReceiptNumber('');
    setReceiptFile(null);
    setEditingPayment(null);
    setShowSuspensionControls(false);
    setSuspensionReason(
      context?.row?.courseSuspension?.reason || context?.row?.suspensionReason || '',
    );
    setSuspensionLevel(
      context?.row?.courseSuspension?.level ||
        context?.row?.suspensionLevel ||
        SUSPENSION_LEVELS.COURSE,
    );
  }, [context, methods, open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      if (event.target?.matches?.('input, textarea, select')) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextRow = getRelativeRow(rows, context?.row?.studentId, direction);
      if (nextRow) onChangeContext?.(nextRow);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [context, onChangeContext, open, rows]);

  if (!context) return null;

  const methodOptions = methods.length
    ? methods
    : [
        { id: 'cash', type: 'cash', label: 'Cash' },
        { id: 'bank_slip', type: 'bank_slip', label: 'Slip / reference' },
        { id: 'card', type: 'card', label: 'Card' },
      ];
  const outstandingLabel = `LKR ${formatAmount(context.row.outstandingTotal)}`;
  const paidLabel = `LKR ${formatAmount(context.row.completedPaymentTotal)}`;
  const currentPaymentLabel = `LKR ${formatAmount(context.cell.paidAmount)} / ${formatAmount(
    context.cell.dueAmount,
  )}`;
  const currentRowIndex = rows.findIndex((row) => row.studentId === context.row.studentId);
  const previousRow = getRelativeRow(rows, context.row.studentId, -1);
  const nextRow = getRelativeRow(rows, context.row.studentId, 1);
  const needsSlip = paymentMethodRequiresSlip(paymentMethod);
  const attendanceRecords = context.row.recentRecords.filter(
    (record) => record.kind === 'attendance',
  );
  const paymentRecords = context.row.recentRecords.filter(
    (record) => record.kind === 'payment',
  );
  const platformSuspended =
    context.row.accessSuspended ||
    context.row.suspensionLevel === SUSPENSION_LEVELS.PLATFORM ||
    String(context.row.status || '').toLowerCase() === 'suspended';
  const activeSuspension =
    suspensionLevel === SUSPENSION_LEVELS.PLATFORM
      ? platformSuspended
        ? {
            level: SUSPENSION_LEVELS.PLATFORM,
            reason: context.row.suspensionReason,
          }
        : null
      : context.row.courseSuspension?.level === suspensionLevel
        ? context.row.courseSuspension
        : null;
  const saveAttendance = (status) => {
    setAttendanceStatus(status);
    onSave({
      attendanceStatus: status,
      attendanceNotes,
      paymentAmount: '',
      paymentMethod,
      receiptNumber,
      receiptFile: null,
      paymentNotes: '',
    });
  };
  const startEditPayment = (record) => {
    if (!record.payment) return;
    setEditingPayment(record.payment);
    setPaymentAmount(String(Number(record.payment.amount || 0)));
    setPaymentMethod(record.payment.paymentMethod || 'cash');
    setReceiptNumber(record.payment.receiptNumber || '');
    setReceiptFile(null);
  };
  const clearPaymentEdit = () => {
    setEditingPayment(null);
    setPaymentAmount('');
    setReceiptNumber('');
    setReceiptFile(null);
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      panelClassName="inline-block w-full max-w-4xl transform overflow-hidden rounded-[22px] bg-white text-left align-bottom shadow-2xl transition-all sm:my-8 sm:align-middle"
    >
      <div className="border-b border-stone-200 bg-[#fafaf9] px-5 py-3 sm:px-6">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4">
          <div className="min-w-0">
            <select
              value={context.row.studentId}
              onChange={(event) => {
                const nextRow = rows.find((row) => row.studentId === event.target.value);
                if (nextRow) onChangeContext?.(nextRow);
              }}
              className="mt-1 max-w-full rounded-lg border-0 bg-transparent p-0 pr-8 text-xl font-semibold tracking-tight text-stone-950 focus:outline-none focus:ring-0"
            >
              {rows.map((row) => (
                <option key={row.studentId} value={row.studentId}>
                  {row.studentName}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-stone-600">
              {context.row.academyStudentId || 'No student ID'} / {context.month.label} / {context.period.label}
            </p>
          </div>
          <div className="hidden shrink-0 items-center justify-center gap-2 sm:flex">
              <button
                type="button"
                onClick={() => previousRow && onChangeContext?.(previousRow)}
                disabled={!previousRow}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-sm font-semibold text-stone-700 disabled:opacity-40"
                aria-label="Previous student"
              >
                ‹
              </button>
              <span className="min-w-[3.5rem] text-center text-xs font-semibold text-stone-500">
                {currentRowIndex + 1}/{rows.length}
              </span>
              <button
                type="button"
                onClick={() => nextRow && onChangeContext?.(nextRow)}
                disabled={!nextRow}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-sm font-semibold text-stone-700 disabled:opacity-40"
                aria-label="Next student"
              >
                ›
              </button>
              <StatusBadge label={attendanceStatus === 'unmarked' ? 'Unmarked' : attendanceStatus} />
              <StatusBadge label={paymentLabel(context.cell)} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition hover:border-stone-300 hover:text-stone-900"
            aria-label="Close modal"
          >
            <FaTimes className="h-3.5 w-3.5" />
          </button>
          <div className="col-span-3 flex items-center justify-center gap-2 sm:hidden">
            <button
              type="button"
              onClick={() => previousRow && onChangeContext?.(previousRow)}
              disabled={!previousRow}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-sm font-semibold text-stone-700 disabled:opacity-40"
              aria-label="Previous student"
            >
              ‹
            </button>
            <span className="min-w-[3.5rem] text-center text-xs font-semibold text-stone-500">
              {currentRowIndex + 1}/{rows.length}
            </span>
            <button
              type="button"
              onClick={() => nextRow && onChangeContext?.(nextRow)}
              disabled={!nextRow}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-sm font-semibold text-stone-700 disabled:opacity-40"
              aria-label="Next student"
            >
              ›
            </button>
            <StatusBadge label={attendanceStatus === 'unmarked' ? 'Unmarked' : attendanceStatus} />
            <StatusBadge label={paymentLabel(context.cell)} />
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-6">
        <section className="rounded-[18px] bg-stone-950 px-4 py-3 text-white">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <SnapshotItem label="Month paid" value={currentPaymentLabel} />
              <SnapshotItem label="Student paid" value={paidLabel} />
              <SnapshotItem label="Outstanding" value={outstandingLabel} />
              <SnapshotItem label="Pending review" value={`LKR ${formatAmount(context.row.pendingPaymentTotal)}`} />
            </div>
            <button
              type="button"
              onClick={() => setShowSuspensionControls((current) => !current)}
              className="inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/10 px-3 text-xs font-semibold text-white transition hover:bg-white/15"
            >
              {showSuspensionControls
                ? 'Hide'
                : activeSuspension
                  ? getSuspensionLabel(activeSuspension.level)
                  : 'Suspend'}
            </button>
          </div>
        </section>

        {showSuspensionControls ? (
          <section className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-950">
                  Student access
                </p>
                <p className="mt-0.5 text-xs text-stone-500">
                  {activeSuspension
                    ? suspensionMessage(activeSuspension, 'this course')
                    : 'Choose whether to block this course, only future lessons, or the whole account.'}
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_auto] lg:w-[38rem]">
                <select
                  value={suspensionLevel}
                  onChange={(event) => setSuspensionLevel(event.target.value)}
                  className="ledger-input min-h-[38px] rounded-xl text-sm"
                >
                  <option value={SUSPENSION_LEVELS.COURSE}>Full course</option>
                  <option value={SUSPENSION_LEVELS.FUTURE_LESSONS}>Future lessons</option>
                  <option value={SUSPENSION_LEVELS.PLATFORM}>Platform account</option>
                </select>
                <input
                  type="text"
                  value={suspensionReason}
                  onChange={(event) => setSuspensionReason(event.target.value)}
                  placeholder="Reason: non-payment, absence, etc."
                  className="ledger-input min-h-[38px] rounded-xl text-sm"
                />
                <Button
                  type="button"
                  variant={activeSuspension ? 'outline' : 'primary'}
                  onClick={() =>
                    onStudentAccessChange?.({
                      row: context.row,
                      suspended: !activeSuspension,
                      reason: suspensionReason,
                      level: suspensionLevel,
                    })
                  }
                  disabled={saving}
                >
                  {activeSuspension
                    ? 'Restore'
                    : `Suspend ${getSuspensionLabel(suspensionLevel).replace(' suspended', '')}`}
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        <OperationBlock
          number="01"
          title="Attendance"
          description="Mark the selected class date and keep a short note when needed."
          aside={
            <div className="grid grid-cols-3 gap-2 text-xs">
              <InlineFact label="Class date" value={context.period.label} />
              <InlineFact label="Month" value={context.month.label} />
              <InlineFact label="Current state" value={attendanceStatus || 'unmarked'} />
            </div>
          }
        >
          <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['present', 'Present'],
                  ['absent', 'Absent'],
                  ['clear', 'Clear'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => saveAttendance(value)}
                    disabled={saving}
                    className={`min-h-[40px] rounded-lg border px-3 text-sm font-semibold transition ${
                      attendanceStatus === value
                        ? 'border-stone-950 bg-stone-950 text-white'
                        : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={attendanceNotes}
              onChange={(event) => setAttendanceNotes(event.target.value)}
              placeholder="Attendance notes"
              className="ledger-input min-h-[40px] rounded-xl py-2"
            />
          </div>
        </OperationBlock>

        <OperationBlock
          number="02"
          title="Payment"
          description="Record a payment against this month and attach the reference used at reception."
          aside={
            <div className="grid grid-cols-2 gap-2 text-xs">
              <InlineFact label="This month" value={currentPaymentLabel} />
              <InlineFact label="Student due" value={outstandingLabel} />
            </div>
          }
        >
          <div className="grid gap-3">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)_42px]">
            <input
              type="number"
              min="0"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
              placeholder="Amount"
              className="ledger-input min-h-[42px] rounded-xl"
            />
              <select
                value={paymentMethod}
                onChange={(event) => {
                  setPaymentMethod(event.target.value);
                  setReceiptFile(null);
                }}
                className="ledger-input min-h-[42px] rounded-xl"
              >
                {methodOptions.map((method) => (
                  <option key={method.id} value={method.type || method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
              <input
                value={receiptNumber}
                onChange={(event) => setReceiptNumber(event.target.value)}
                placeholder="Reference"
                className="ledger-input min-h-[42px] rounded-xl"
              />
              <button
                type="button"
                onClick={() => {
                  if (editingPayment) {
                    const result = onUpdatePayment?.(editingPayment, {
                        paymentAmount,
                        paymentMethod,
                        receiptNumber,
                        receiptFile,
                        paymentNotes: editingPayment.notes || '',
                    });
                    Promise.resolve(result).then(clearPaymentEdit);
                    return;
                  }
                  onSave({
                    attendanceStatus: '',
                    attendanceNotes,
                    paymentAmount,
                    paymentMethod,
                    receiptNumber,
                    receiptFile,
                    paymentNotes: '',
                  });
                }}
                disabled={saving || Number(paymentAmount || 0) <= 0}
                className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-stone-950 text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300"
                title={editingPayment ? 'Update payment record' : 'Save payment record'}
                aria-label={editingPayment ? 'Update payment record' : 'Save payment record'}
              >
                <FaPaperPlane className="h-3.5 w-3.5" />
              </button>
            </div>
            {editingPayment ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <span className="font-semibold">
                  Editing payment from {formatShortDate(new Date(editingPayment.paymentDate || editingPayment.createdAt || 0))}
                </span>
                <button
                  type="button"
                  onClick={clearPaymentEdit}
                  className="font-semibold text-amber-900 underline"
                >
                  Cancel edit
                </button>
              </div>
            ) : null}
            {needsSlip ? (
              <label className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-3 text-sm text-stone-600 lg:col-span-2">
                <span className="block font-semibold text-stone-900">Attach bank slip</span>
                <span className="mt-0.5 block text-xs">
                  Required for bank deposit or transfer methods.
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => setReceiptFile(event.target.files?.[0] || null)}
                  className="mt-2 block w-full text-xs text-stone-600 file:mr-3 file:rounded-lg file:border-0 file:bg-stone-900 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white"
                />
                {receiptFile ? (
                  <span className="mt-2 block truncate text-xs font-semibold text-stone-900">
                    {receiptFile.name}
                  </span>
                ) : null}
              </label>
            ) : null}
          </div>
        </OperationBlock>

        <section className="border-t border-stone-200 pt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
              <div>
              <p className="text-sm font-semibold text-stone-950">Recent activity</p>
              <p className="mt-0.5 text-xs text-stone-500">Latest payment and attendance records for this batch.</p>
              </div>
            <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600 ring-1 ring-stone-200">
              {context.row.recentRecords.length}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <RecentRecordColumn title="Attendance" records={attendanceRecords} />
            <RecentRecordColumn
              title="Payments"
              records={paymentRecords}
              onEdit={startEditPayment}
              onDelete={(record) => onDeletePayment?.(record.payment)}
            />
          </div>
        </section>
      </div>
      {error ? <p className="mx-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
      <div className="flex flex-col-reverse gap-2 border-t border-stone-200 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

const StatusBadge = ({ label }) => (
  <span className="inline-flex min-h-[28px] items-center rounded-full bg-stone-100 px-3 text-xs font-semibold text-stone-700 ring-1 ring-stone-200">
    {label}
  </span>
);

const SnapshotItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">
      {label}
    </p>
    <p className="mt-1 truncate text-sm font-semibold text-white">{value}</p>
  </div>
);

const InlineFact = ({ label, value }) => (
  <div className="min-w-0 rounded-lg bg-stone-50 px-3 py-2">
    <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </p>
    <p className="mt-1 truncate font-semibold text-stone-950">{value}</p>
  </div>
);

const OperationBlock = ({ number, title, description, aside, children }) => (
  <section className="border-t border-stone-200 pt-4">
    <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 gap-3">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-950 text-xs font-semibold text-white">
          {number}
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-stone-950">{title}</p>
          <p className="mt-0.5 text-sm leading-5 text-stone-500">{description}</p>
        </div>
      </div>
      <div className="w-full lg:w-[22rem]">{aside}</div>
    </div>
    {children}
  </section>
);

const RecentRecordColumn = ({ title, records, onEdit, onDelete }) => (
  <div className="min-w-0">
    <div className="mb-1.5 flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
        {title}
      </p>
      <span className="text-xs font-semibold text-stone-500">{records.length}</span>
    </div>
    <div className="max-h-[8rem] space-y-1 overflow-y-auto pr-1">
      {records.length ? (
        records.slice(0, 8).map((record) => (
          <div
            key={record.id}
            className="flex min-h-[28px] items-center justify-between gap-2 rounded-md border border-stone-200 bg-stone-50 px-2 text-xs"
            title={`${record.title} - ${record.detail}`}
          >
            <span className="min-w-0 truncate font-semibold text-stone-900">
              {record.title}
            </span>
            <span className="shrink-0 truncate text-stone-500">{record.detail}</span>
            {record.kind === 'payment' ? (
              <span className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => onEdit?.(record)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded bg-white text-stone-700 ring-1 ring-stone-200 hover:bg-stone-100"
                  title="Edit payment"
                  aria-label="Edit payment"
                >
                  <FaPen className="h-2.5 w-2.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete?.(record)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded bg-white text-red-600 ring-1 ring-red-100 hover:bg-red-50"
                  title="Remove payment"
                  aria-label="Remove payment"
                >
                  <FaTrash className="h-2.5 w-2.5" />
                </button>
              </span>
            ) : null}
          </div>
        ))
      ) : (
        <p className="rounded-md border border-dashed border-stone-300 bg-white px-2 py-2 text-xs text-stone-500">
          No records
        </p>
      )}
    </div>
  </div>
);

const getRelativeRow = (rows, studentId, direction) => {
  const index = rows.findIndex((row) => row.studentId === studentId);
  if (index < 0) return null;
  return rows[index + direction] || null;
};

const paymentMethodRequiresSlip = (method = '') => {
  const normalized = String(method).toLowerCase();
  return (
    normalized.includes('bank') ||
    normalized.includes('transfer') ||
    normalized.includes('deposit') ||
    normalized.includes('slip')
  );
};

const CompactField = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    {children}
  </label>
);

const CompactStat = ({ label, value, tone = 'neutral' }) => {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-700'
      : tone === 'warning'
        ? 'text-amber-700'
        : 'text-stone-950';
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
        {label}
      </p>
      <p className={`mt-0.5 truncate text-base font-semibold ${toneClass}`}>
        {value}
      </p>
    </div>
  );
};

const CourseBatchSummary = ({ course, batch, periods, onSaveFees, saving, error }) => {
  const [editing, setEditing] = useState(false);
  const [fullFeeDraft, setFullFeeDraft] = useState('');
  const [registrationFeeDraft, setRegistrationFeeDraft] = useState('');

  useEffect(() => {
    setFullFeeDraft(String(Number(course?.fees?.fullCourseFee || course?.oneoffPayment || 0)));
    setRegistrationFeeDraft(String(Number(course?.fees?.registrationFee || 0)));
  }, [course]);

  if (!course || !batch) return null;
  const fullFee = Number(course?.fees?.fullCourseFee || course?.oneoffPayment || 0);
  const registrationFee = Number(course?.fees?.registrationFee || 0);
  const monthlyFee =
    periods.length > 0 ? Math.max(0, fullFee - registrationFee) / periods.length : 0;
  const startDate = parseBatchDate(batch.startDate);
  const endDate = periods[periods.length - 1]?.end || null;
  const durationLabel = course.duration?.isOneDay
    ? 'One day'
    : `${Number(course.duration?.months || periods.length || 0)} months`;

  return (
    <div className="mt-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs">
      <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_0.8fr_1.2fr_auto] md:items-end">
        {editing ? (
          <>
            <CompactField label="Course fee">
              <input
                type="number"
                min="0"
                value={fullFeeDraft}
                onChange={(event) => setFullFeeDraft(event.target.value)}
                className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
              />
            </CompactField>
            <CompactField label="Registration">
              <input
                type="number"
                min="0"
                value={registrationFeeDraft}
                onChange={(event) => setRegistrationFeeDraft(event.target.value)}
                className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
              />
            </CompactField>
          </>
        ) : (
          <>
            <SummaryItem label="Course fee" value={`LKR ${formatAmount(fullFee)}`} />
            <SummaryItem label="Registration" value={`LKR ${formatAmount(registrationFee)}`} />
          </>
        )}
        <SummaryItem label="Monthly fee" value={`LKR ${formatAmount(monthlyFee)}`} />
        <SummaryItem label="Duration" value={durationLabel} />
        <SummaryItem
          label="Batch dates"
          value={`${formatShortDate(startDate)} - ${formatShortDate(endDate)}`}
        />
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button
                type="button"
                size="sm"
                onClick={async () => {
                  const saved = await onSaveFees({
                    fullFee: fullFeeDraft,
                    registrationFee: registrationFeeDraft,
                  });
                  if (saved) setEditing(false);
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit fees
            </Button>
          )}
        </div>
      </div>
      {error ? <p className="mt-2 text-xs font-semibold text-red-700">{error}</p> : null}
    </div>
  );
};

const SummaryItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </p>
    <p className="mt-0.5 truncate font-semibold text-stone-900">{value}</p>
  </div>
);

const Legend = () => (
  <div className="hidden flex-wrap items-center gap-2 text-xs sm:flex">
    <MiniChip tone="success">Paid</MiniChip>
    <MiniChip tone="info">Partial</MiniChip>
    <MiniChip tone="warning">Due</MiniChip>
    <MiniChip tone="neutral">Upcoming</MiniChip>
  </div>
);

const MiniChip = ({ tone = 'neutral', children, className = '' }) => {
  const classes = {
    success: 'bg-emerald-200 text-emerald-950',
    info: 'bg-[#fbcfe8] text-[#9d174d]',
    warning: 'bg-amber-200 text-amber-950',
    danger: 'bg-red-200 text-red-950',
    neutral: 'bg-stone-200 text-stone-800',
    amount: 'bg-stone-100 text-stone-900',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${classes[tone]} ${className}`}>
      {children}
    </span>
  );
};

const summarizeAttendance = (weekCells) => {
  if (weekCells.some((cell) => cell.attendanceStatus === 'present')) return 'present';
  if (weekCells.some((cell) => cell.attendanceStatus === 'absent')) return 'absent';
  return 'unmarked';
};

const mergeAttendanceOverrides = (attendanceByStudent, overrides) => {
  if (!Object.keys(overrides || {}).length) return attendanceByStudent;
  const merged = { ...(attendanceByStudent || {}) };
  Object.values(overrides).forEach((record) => {
    const list = merged[record.studentId] || [];
    const withoutSamePeriod = list.filter(
      (item) =>
        !(
          String(item.courseId || '') === String(record.courseId || '') &&
          (!record.batchId || !item.batchId || String(item.batchId) === String(record.batchId)) &&
          item.periodStart === record.periodStart
        ),
    );
    merged[record.studentId] =
      record.status === 'clear'
        ? withoutSamePeriod
        : [...withoutSamePeriod, record];
  });
  return merged;
};

const applyAttendanceOverride = (current, payload) => {
  const overrideKey = buildAttendanceOverrideKey(payload);
  const next = { ...current };
  if (payload.status === 'clear') {
    next[overrideKey] = { ...payload, overrideKey, id: overrideKey };
    return next;
  }
  const timestamp = new Date().toISOString();
  next[overrideKey] = {
    ...payload,
    overrideKey,
    id: overrideKey,
    date: payload.periodStart,
    markedAt: timestamp,
    markedById: payload.operator?.uid || '',
    markedByName:
      payload.operator?.displayName ||
      `${payload.operator?.firstName || ''} ${payload.operator?.lastName || ''}`.trim() ||
      payload.operator?.email ||
      'Admin',
    source: 'admin_batch_ledger',
  };
  return next;
};

const buildAttendanceOverrideKey = ({ studentId, courseId, batchId, periodStart }) =>
  `${studentId}::${courseId}::${batchId || ''}::${periodStart}`;

const sortRows = (rows, sortBy) => {
  const sorted = [...rows];
  const compareText = (a = '', b = '') => a.localeCompare(b, undefined, { numeric: true });
  const compareNumber = (a = 0, b = 0) => Number(a || 0) - Number(b || 0);

  if (sortBy === 'id-asc') {
    return sorted.sort((a, b) => compareText(a.academyStudentId, b.academyStudentId));
  }
  if (sortBy === 'paid-desc') {
    return sorted.sort(
      (a, b) =>
        compareNumber(b.completedPaymentTotal, a.completedPaymentTotal) ||
        compareText(a.studentName, b.studentName),
    );
  }
  if (sortBy === 'paid-asc') {
    return sorted.sort(
      (a, b) =>
        compareNumber(a.completedPaymentTotal, b.completedPaymentTotal) ||
        compareText(a.studentName, b.studentName),
    );
  }
  if (sortBy === 'due-desc') {
    return sorted.sort(
      (a, b) =>
        compareNumber(b.outstandingTotal, a.outstandingTotal) ||
        compareText(a.studentName, b.studentName),
    );
  }
  if (sortBy === 'due-asc') {
    return sorted.sort(
      (a, b) =>
        compareNumber(a.outstandingTotal, b.outstandingTotal) ||
        compareText(a.studentName, b.studentName),
    );
  }
  return sorted.sort((a, b) => compareText(a.studentName, b.studentName));
};

const buildStudentRows = ({
  course,
  batch,
  periods,
  students,
  users,
  payments,
  paymentRequests,
  attendanceByStudent,
}) => {
  const duePlanSummary = buildDuePlan(course, periods);
  const duePlan = duePlanSummary.periods;
  const enrolled = batch.enrolledStudents || [];
  const batchId = batch.batchId ?? batch.batchIndex;
  const paymentsByLedgerKey = groupRecordsByLedgerKey(payments);
  const requestsByLedgerKey = groupRecordsByLedgerKey(paymentRequests);

  return enrolled
    .map((enrollment) => {
      const student = students[enrollment.studentId] || {};
      const user = users[enrollment.studentId] || {};
      const studentName =
        formatStudentName(student) ||
        formatStudentName(user) ||
        enrollment.studentName ||
        'Student';
      const relatedPayments = getGroupedLedgerRecords(
        paymentsByLedgerKey,
        enrollment.studentId,
        course.id,
        batchId,
      );
      const monthlyPayments = relatedPayments.filter(isMonthlyLedgerPayment);
      const completedPaymentTotal = monthlyPayments
        .filter((payment) => payment.status === 'completed')
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
      const pendingPaymentTotal = monthlyPayments
        .filter((payment) => payment.status === 'pending')
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
      const openRequestTotal = getGroupedLedgerRecords(
        requestsByLedgerKey,
        enrollment.studentId,
        course.id,
        batchId,
      )
        .filter(
          (request) => ['pending', 'processing'].includes(request.status),
        )
        .reduce((sum, request) => sum + Number(request.amount || 0), 0);
      const allocation = allocatePayments(
        duePlan,
        completedPaymentTotal,
        duePlanSummary.registrationFee,
      );
      const attendance = (attendanceByStudent[enrollment.studentId] || []).filter(
        (record) => recordMatches(record, enrollment.studentId, course.id, batchId),
      );
      const courseSuspension =
        student.courseSuspensions?.[course.id] ||
        user.courseSuspensions?.[course.id] ||
        enrollment.courseSuspensions?.[course.id] ||
        null;
      const recentRecords = buildRecentRecords(relatedPayments, attendance, course.id, batchId);
      const cells = periods.map((period, index) => {
        const due = duePlan[index];
        const paidAmount = allocation[index] || 0;
        const weekCells = period.weeks.map((week) => {
          const attendanceRecord = findAttendanceForPeriod(
            attendance,
            course.id,
            batchId,
            week,
          );
          return {
            period: week,
            weekNumber: week.weekNumber,
            attendanceRecord,
            attendanceStatus: attendanceRecord?.status || 'unmarked',
            isCurrent: dateInRange(new Date(), week.start, week.end),
            isCurrentOrPast: week.start <= new Date(),
          };
        });
        return {
          period,
          dueAmount: due.amount,
          paidAmount,
          pendingPaymentTotal,
          weekCells,
          attendanceStatus: summarizeAttendance(weekCells),
          paymentStatus: resolvePaymentStatus(due.amount, paidAmount, period),
          isCurrent: dateInRange(new Date(), period.start, period.end),
          isCurrentOrPast: period.start <= new Date(),
        };
      });

      return {
        studentId: enrollment.studentId,
        enrollment,
        studentName,
        academyStudentId: student.academyStudentId || user.academyStudentId || '',
        phone: student.personalDetails?.phone || user.phone || student.phone || '',
        status: student.status || user.status || 'active',
        accessSuspended: Boolean(student.accessSuspended || user.accessSuspended),
        suspensionLevel: student.suspensionLevel || user.suspensionLevel || '',
        suspensionReason: student.suspensionReason || user.suspensionReason || '',
        courseSuspension,
        branch:
          student.branchName ||
          student.personalDetails?.branchName ||
          batch.branchName ||
          batch.branch ||
          '',
        cells,
        recentRecords,
        completedPaymentTotal,
        pendingPaymentTotal,
        openRequestTotal,
        totalDue: duePlanSummary.totalDue,
        outstandingTotal: Math.max(
          0,
          duePlanSummary.totalDue - completedPaymentTotal,
        ),
        paidPeriodCount: cells.filter((cell) => cell.paymentStatus === 'paid').length,
      };
    })
    .sort((a, b) => a.studentName.localeCompare(b.studentName));
};

const buildLedgerPeriods = (course, batch) => {
  if (!course || !batch) return [];
  const start = parseBatchDate(batch.startDate);
  const monthCount = course.duration?.isOneDay
    ? 1
    : Math.max(0, Number(course.duration?.months || 0));
  if (!start || !monthCount) return [];

  return Array.from({ length: monthCount }, (_, index) => {
    const periodStart = new Date(start);
    periodStart.setMonth(start.getMonth() + index);
    const periodEnd = new Date(periodStart);
    periodEnd.setMonth(periodStart.getMonth() + 1);
    periodEnd.setDate(periodEnd.getDate() - 1);
    const classDates = buildClassDatesForPeriod(periodStart, periodEnd, batch.schedule?.days);
    const weeks = classDates.map((classDate, weekIndex) => {
      return {
        key: `${toDateKey(classDate)}-${index}-${weekIndex}`,
        start: classDate,
        end: classDate,
        startKey: toDateKey(classDate),
        endKey: toDateKey(classDate),
        weekNumber: weekIndex + 1,
        label: classDate.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        }),
      };
    });
    return {
      key: `${toDateKey(periodStart)}-${index}`,
      start: periodStart,
      end: periodEnd,
      startKey: toDateKey(periodStart),
      endKey: toDateKey(periodEnd),
      label: periodStart.toLocaleDateString([], {
        month: 'short',
        year: 'numeric',
      }),
      monthNumber: index + 1,
      weeks,
    };
  });
};

const buildClassDatesForPeriod = (periodStart, periodEnd, scheduleDays = []) => {
  const activeDays = normalizeScheduleDays(scheduleDays);
  if (!activeDays.length) {
    return Array.from({ length: 4 }, (_, index) => {
      const date = new Date(periodStart.getTime() + index * 7 * MS_PER_DAY);
      return date <= periodEnd ? startOfDay(date) : null;
    }).filter(Boolean);
  }

  return Array.from({ length: 4 }, (_, weekIndex) => {
    const weekStart = startOfDay(
      new Date(periodStart.getTime() + weekIndex * 7 * MS_PER_DAY),
    );
    if (weekStart > periodEnd) return null;
    const weekEnd = startOfDay(
      new Date(Math.min(periodEnd.getTime(), weekStart.getTime() + 6 * MS_PER_DAY)),
    );
    return firstScheduledDateInRange(weekStart, weekEnd, activeDays) || weekStart;
  }).filter(Boolean);
};

const firstScheduledDateInRange = (start, rangeEnd, activeDays) => {
  const cursor = startOfDay(start);
  const end = startOfDay(rangeEnd);
  while (cursor <= end) {
    if (activeDays.includes(cursor.getDay())) {
      return new Date(cursor);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
};

const normalizeScheduleDays = (days = []) =>
  (days || [])
    .map((day) => DAY_INDEX[String(day).slice(0, 3).toUpperCase()])
    .filter((day) => Number.isInteger(day))
    .sort((a, b) => a - b);

const buildDuePlan = (course, periods) => {
  const fullFee = Number(course?.fees?.fullCourseFee || course?.oneoffPayment || 0);
  const registrationFee = Number(course?.fees?.registrationFee || 0);
  const tuitionTotal = Math.max(0, fullFee - registrationFee);
  const periodAmount = periods.length ? tuitionTotal / periods.length : 0;
  return {
    registrationFee,
    totalDue: tuitionTotal,
    periods: periods.map((period) => ({ period, amount: periodAmount })),
  };
};

const allocatePayments = (duePlan, completedPaymentTotal) => {
  let remaining = Math.max(0, completedPaymentTotal);
  return duePlan.map((due) => {
    const paid = Math.min(remaining, due.amount);
    remaining -= paid;
    return paid;
  });
};

const resolvePaymentStatus = (dueAmount, paidAmount, period) => {
  if (paidAmount >= dueAmount && dueAmount > 0) return 'paid';
  if (paidAmount > 0) return 'partial';
  return period.start <= new Date() ? 'due' : 'upcoming';
};

const findAttendanceForPeriod = (records, courseId, batchId, period) =>
  records
    .filter((record) => {
      if (String(record.courseId || '') !== String(courseId)) return false;
      if (batchId && record.batchId && String(record.batchId) !== String(batchId)) {
        return false;
      }
      if (record.periodStart) return record.periodStart === period.startKey;
      const date = new Date(record.date || record.markedAt || 0);
      return dateInRange(date, period.start, period.end);
    })
    .sort((a, b) => new Date(b.markedAt || b.date || 0) - new Date(a.markedAt || a.date || 0))[0];

const buildRecentRecords = (payments, attendance, courseId, batchId) => {
  const paymentRecords = payments.map((payment) => ({
    id: `payment-${payment.id}`,
    kind: 'payment',
    payment,
    at: payment.paymentDate || payment.createdAt,
    title: `LKR ${formatAmount(payment.amount)}`,
    detail: `${payment.status || 'recorded'} / ${formatShortDate(new Date(payment.paymentDate || payment.createdAt || 0))}`,
  }));
  const attendanceRecords = attendance
    .filter(
      (record) =>
        String(record.courseId || '') === String(courseId) &&
        (!batchId || !record.batchId || String(record.batchId) === String(batchId)),
    )
    .map((record) => ({
      id: `attendance-${record.id}`,
      kind: 'attendance',
      at: record.markedAt || record.date,
      title: record.status || 'marked',
      detail: formatShortDate(new Date(record.markedAt || record.date || 0)),
    }));
  return [...paymentRecords, ...attendanceRecords]
    .filter((record) => record.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at));
};

const isMonthlyLedgerPayment = (payment) => {
  const type = String(payment?.paymentType || '').toLowerCase();
  if (type === 'registration') return false;
  return true;
};

const groupRecordsByLedgerKey = (records = []) =>
  records.reduce((map, record) => {
    if (!record?.studentId || !record?.courseId) return map;
    const key = buildLedgerRecordKey(record.studentId, record.courseId, record.batchId);
    if (!map[key]) map[key] = [];
    map[key].push(record);
    if (record.batchId) {
      const courseOnlyKey = buildLedgerRecordKey(record.studentId, record.courseId, '');
      if (!map[courseOnlyKey]) map[courseOnlyKey] = [];
    }
    return map;
  }, {});

const getGroupedLedgerRecords = (map, studentId, courseId, batchId) => [
  ...(map[buildLedgerRecordKey(studentId, courseId, batchId)] || []),
  ...(map[buildLedgerRecordKey(studentId, courseId, '')] || []),
];

const buildLedgerRecordKey = (studentId, courseId, batchId) =>
  `${studentId || ''}::${courseId || ''}::${batchId || ''}`;

const recordMatches = (record, studentId, courseId, batchId) => {
  const sameStudent = String(record.studentId || '') === String(studentId);
  const sameCourse = String(record.courseId || '') === String(courseId);
  const sameBatch = !batchId || !record.batchId || String(record.batchId) === String(batchId);
  return sameStudent && sameCourse && sameBatch;
};

const parseBatchDate = (value) => {
  if (!value) return null;
  if (value.year && value.month && value.date) {
    return startOfDay(new Date(value.year, value.month - 1, value.date));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const toDateKey = (date) => date.toISOString().split('T')[0];

const dateInRange = (date, start, end) => {
  if (Number.isNaN(date.getTime())) return false;
  return date >= start && date <= new Date(end.getTime() + MS_PER_DAY - 1);
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatShortDate = (date) =>
  !date || Number.isNaN(date.getTime())
    ? 'Not set'
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });

const attendanceSlotClass = (status) => {
  if (status === 'present') return 'border-emerald-400 bg-emerald-200 text-emerald-950';
  if (status === 'absent') return 'border-red-400 bg-red-200 text-red-950';
  return 'border-stone-300 bg-white text-stone-500';
};

const attendanceSlotText = (weekCell) => {
  if (weekCell.attendanceStatus === 'present') return 'P';
  if (weekCell.attendanceStatus === 'absent') return 'A';
  return weekCell.period.start.getDate();
};

const paymentLabel = (cell) => {
  if (cell.paymentStatus === 'paid') return 'Paid';
  if (cell.paymentStatus === 'partial') return 'Partial';
  if (cell.paymentStatus === 'due') return 'Due';
  return 'Upcoming';
};

const cellClass = (status) => {
  if (status === 'paid') return 'border-emerald-400 bg-emerald-100';
  if (status === 'partial') return 'border-[#db2777] bg-[#fbcfe8]';
  if (status === 'due') return 'border-amber-400 bg-amber-100';
  return 'border-stone-300 bg-stone-100';
};

export default BatchStudentManagement;
